/* eslint-disable no-underscore-dangle */
import * as React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Divider,
  Switch, Typography,styled
} from '@mui/material';
import { Refresh } from '@material-ui/icons';

import Modal from 'components/common/Modal';
import { useTranslation } from 'components/providers/TranslationProvider';
import Toast from 'components/common/Toast';
import useOrganizations from 'api/hooks/useOrganizations';
import { useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { gridMarginStyle } from '../styles/formModal';
import CustomSkeleton from '../../../common/CustomSkeleton';
import { SelectField } from '../../../common/ReactHooksFormFields';
import { useAuth } from '../../../providers/AuthProvider';
import { giveMeId } from '../../../../utils/supportComponents';
import useSchemes from '../../../../api/hooks/useSchemes';
import useCopyScheme from '../../../../api/hooks/useCopyScheme';
import { setPrivateLoading } from '../../../../store/appSlice';
import { useTheme } from '../../../providers/CustomThemeProvider';
import useUsers from '../../../../api/hooks/useUsers';
import { MuiBtnAction, MuiBtnClose } from '../dataset/styles/formModal';
import {lighten} from "@mui/material/styles";
import MuiCircularProgress from "@mui/material/CircularProgress";

const schema = yup.object().shape({
  userToId: yup.string().required(),
  selectedSchemeId: yup.string().required(),
});

const CopyModal = ({ scenario, open, onClose }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dispatch = useDispatch();
  const orgId = location.pathname.split('/')[2];
  const { data: orgs, isLoading: isLoadingOrgs } = useOrganizations();
  const [error, setError] = React.useState('');
  const [copyTarget,setCopyTarget] = React.useState('organizations');
  const [openToast, setOpenToast] = React.useState(false);
  const { user } = useAuth();
  const id = giveMeId(location.pathname, user);
  const { data: schemes, isLoading: isLoadingSchemes } = useSchemes(id);
  const {data: users, isLoading: isLoadingUsers} = useUsers(orgId);
  const {theme} = useTheme()
  const [targetCantidates, setTargetCantidates] = React.useState(users);

  const MuiSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
  ))(({ theme, colorSecondary }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
      padding: 0,
      margin: 2,
      transitionDuration: '300ms',
      '&.Mui-checked': {
        transform: 'translateX(16px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          backgroundColor: colorSecondary,
          opacity: 1,
          border: 0,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
          opacity: 0.5,
        },
      },
      '&.Mui-focusVisible .MuiSwitch-thumb': {
        color: theme.palette.primary.main,
        border: '6px solid #fff',
      },
      '&.Mui-disabled .MuiSwitch-thumb': {
        color:
          theme.palette.mode === 'light'
            ? theme.palette.grey[100]
            : theme.palette.grey[600],
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: 22,
      height: 22,
    },
    '& .MuiSwitch-track': {
      borderRadius: 26 / 2,
      backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
      opacity: 1,
      transition: theme.transitions.create(['background-color'], {
        duration: 500,
      }),
    },
  }));
  const commonStyle = {
    fontFamily:'Montserrat',
    fontWeight:400,
    textTransform:'uppercase',
    fontSize:'12px'
  }

  const MuiRefreshIcon = styled(Refresh, {
    shouldForwardProp: (prop) => prop !== 'loading'
  })(({ theme,loading }) => ({
    width: '26px',
    height: '26px',
    marginRight:'5px',
    padding: 3,
    borderRadius:'50%',
    border: `1px solid ${lighten(theme.palette.primary.main, 0.6)}`,
    align: 'center',
    cursor:'pointer',
    backgroundColor: 'transparent',
    color:theme.palette.primary.main,
    animation: loading ? 'rotate 2s linear infinite' : 'none',
    '@keyframes rotate': {
      from: {
        transform: 'rotate(0deg)',
      },
      to: {
        transform: 'rotate(360deg)',
      },
    },
  }));
  const getDefaultValues = () => {
    return scenario
      ? {
          userToId: scenario.userToId,
          selectedSchemeId: scenario.selectedSchemeId,
        }
      : {
          userToId: '',
          selectedSchemeId: '',
        };
  };

  const {
    mutateAsync: copyScheme,
    isError: copyIsError,
    isSuccess: copyIsSuccess,
    error: copyError,
    isLoading: copyIsLoading,
  } = useCopyScheme();

  React.useEffect(() => {
    if (copyIsError) {
      dispatch(setPrivateLoading(false));
      setError(copyError.data?.error ? t(copyError.data.error) : JSON.stringify(copyError));
      setOpenToast(true);
    }
  }, [copyIsError, copyError, t]);

  React.useEffect(() => {
    if (copyIsSuccess) {
      onClose(copyIsSuccess);
    }
  }, [copyIsSuccess]);

  React.useEffect(() => {
    if(copyTarget==='organizations'){
      setTargetCantidates(orgs);
    }else {
      setTargetCantidates(users);
    }
  },[copyTarget,isLoadingOrgs,isLoadingUsers]);

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(),
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isValid },
  } = methods;

  const copyScenario = (data) => {
    const scenarioToCopy = {
      userFromId: id,
      userToId: data.userToId,
      selectedSchemeId: data.selectedSchemeId,
    };

    copyScheme(scenarioToCopy);
  };

  const copyForm = (
    <Container display='flex' style={{padding:'16px'}}>
      <Grid item xs={12} >
        {isLoadingOrgs ? (
          <CustomSkeleton />
        ) : (
          <Grid container>
            <Grid container  style={gridMarginStyle} alignContent='center' item xs={12}>
              <Typography  variant='caption'>{t('copy_target')}</Typography>
            </Grid>
            <Grid container style={{ marginBottom: 15 }} alignContent='left' item xs={2}>
              <MuiSwitch colorSecondary={theme.palette.secondary.main}
                         checked={copyTarget === 'users'}
                         onChange={() => setCopyTarget(prevState => prevState==='users'?'organizations':'users')}
              />
            </Grid>
            <Grid container alignContent={'left'} item xs={9} paddingTop={1}>
              <Typography sx={{
                ...commonStyle,
                color: copyTarget === 'organizations' ? theme.palette.secondary.main : 'black'}}>
                {t('orgs')}
              </Typography>
              <span style={{...commonStyle, marginLeft:'3px'}}>/</span>
              <Typography sx={{
                ...commonStyle,
                marginLeft:'3px',
                color: copyTarget === 'users' ? theme.palette.secondary.main : 'black'}}>
                {t('users')}
              </Typography>
            </Grid>
      <Grid item xs={12}>
              {isLoadingSchemes ? (
          <CustomSkeleton />
              ) : (
                <SelectField name='selectedSchemeId' variant='outlined' label={t('select_scenario')} fullWidth>
                  <Divider light value=' ' />
                  {schemes?.schemes.map((o) => {
                    return (
                      <MenuItem key={o.id} value={o.id}>
                        {o.name}
                      </MenuItem>
                    );
                  })}
                </SelectField>
              )}
            </Grid>
            <SelectField name='userToId' variant='outlined' label={t('copy_to')} fullWidth>
              <Divider light value=' ' />
              {targetCantidates?.map((u) => {
                return (
                  <MenuItem key={u._id} value={u._id}>
                    <Typography  variant='body1' fontFamily='Montserrat' fontSize={16}  letterSpacing={0.5}>
                      {copyTarget==='users'?u.email:u.name}
                    </Typography>
                  </MenuItem>
                );
              })}
            </SelectField>
          </Grid>
        )}
      </Grid>
    </Container>
  );

  const handleCloseToast = () => setOpenToast(false);

  const onSubmit = handleSubmit((data) => copyScenario(data));

  const actions = (
    <>
      {copyIsLoading ? <LinearProgress /> : null}
      <MuiBtnClose onClick={onClose} color='primary' variant='text'>
        {t('cancel_btn')}
      </MuiBtnClose>
      <MuiBtnAction
        variant='outlined'
        onClick={onSubmit}
        disabled={isLoadingSchemes || isLoadingOrgs || !isValid || copyIsLoading}
      >
        {copyIsLoading && <MuiCircularProgress loading={true} size={20} style={{marginLeft: '-15px', marginRight: '5px'}}/>}
        {!isLoadingSchemes || !isLoadingOrgs ?
          <Typography  fontFamily='Montserrat' fontSize={14}  letterSpacing={0.25}>
            {t('copy')}
          </Typography>
          : <Typography  fontFamily='Montserrat' fontSize={14}  letterSpacing={0.25}>
            {t('processing')}
          </Typography>}
      </MuiBtnAction>
    </>
  );

  return (
    <FormProvider {...methods}>
      <Toast
        open={openToast}
        handleClose={handleCloseToast}
        severity='error'
        vertical='top'
        horizontal='center'
        message={error}
      />
      <form>
        <Modal open={open} onClose={onClose} title={t('copy_schemes')} actions={actions}>
          {copyForm}
        </Modal>
      </form>
    </FormProvider>
  );
};

export default CopyModal;
